export const PARAMETER_TYPES = ['Facilities', 'Pharmacies', 'User', 'Custom'];

export const DATA_TYPES = ['Text', 'Integer', 'Decimal', 'Date', 'Boolean', 'List'];

export const CUSTOM_DATA_TYPES = DATA_TYPES.filter((type) => type !== 'List');

export const DEFAULT_INTERNAL_NAMES = { Facilities: 'facilities', Pharmacies: 'pharmacies', User: 'user_name' };

export const INTERNAL_NAME_PATTERN = /^[a-z_][a-z0-9_]*$/;

const SQL_TEXT_AND_COMMENTS = /'(?:[^']|'')*'|"(?:[^"]|"")*"|--[^\n]*|\/\*[\s\S]*?\*\/|\$(\w*)\$[\s\S]*?\$\1\$/g;

const SQL_FORMAT_PROTECT = /'(?:[^']|'')*'|"(?:[^"]|"")*"|\/\*[\s\S]*?\*\/|\$(\w*)\$[\s\S]*?\$\1\$/g;

const SQL_MAX_LINE = 90;

function toSqlCode(sql) {
    return (sql || '').replace(SQL_TEXT_AND_COMMENTS, ' ').trim();
}

export function getSqlError(sql) {
    const code = toSqlCode(sql);
    if (code.replace(/;+$/, '').includes(';')) return 'Only one SQL statement is allowed.';
    if (!/^\(*\s*(select|with|call)\b/i.test(code))
        return 'A report can only read data, so the SQL must start with SELECT, WITH or CALL.';
    return null;
}

export function getSqlParameterNames(sql) {
    const pattern = /(^|[^@\w])@([A-Za-z_][A-Za-z0-9_]*)/g;
    const code = toSqlCode(sql);
    const names = [];
    let match;
    while ((match = pattern.exec(code)) !== null) {
        if (!names.includes(match[2])) names.push(match[2]);
    }
    return names;
}

function findOuterCall(text) {
    let depth = 0;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (c === '(' && depth === 0 && i > 0 && /[A-Za-z0-9_]/.test(text[i - 1])) {
            let d = 0;
            for (let j = i; j < text.length; j++) {
                if (text[j] === '(') d += 1;
                else if (text[j] === ')') {
                    d -= 1;
                    if (d === 0) {
                        if (text.slice(i + 1, j).replace(/\[[^\]]*\]/g, '').includes(',')) return { open: i, close: j };
                        break;
                    }
                }
            }
        }
        if (c === '(') depth += 1;
        else if (c === ')') depth -= 1;
    }
    return { open: -1, close: -1 };
}

function splitTopLevelCommas(inner) {
    const parts = [];
    let depth = 0;
    let last = 0;
    for (let i = 0; i < inner.length; i++) {
        const c = inner[i];
        if (c === '(' || c === '[') depth += 1;
        else if (c === ')' || c === ']') depth -= 1;
        else if (c === ',' && depth === 0) {
            parts.push(inner.slice(last, i).trim());
            last = i + 1;
        }
    }
    parts.push(inner.slice(last).trim());
    return parts;
}

export function formatSql(sql) {
    if (!sql || !sql.trim()) return sql || '';
    try {
        const lines = sql.replace(/\r\n/g, '\n').split('\n');
        const header = [];
        let start = 0;
        while (start < lines.length && (lines[start].trim() === '' || lines[start].trim().startsWith('--'))) {
            header.push(lines[start].trim());
            start += 1;
        }

        let body = lines.slice(start).join('\n');
        if (body.includes('--')) return sql;

        const store = [];
        body = body
            .replace(SQL_FORMAT_PROTECT, (m) => `@@SQLFMT${store.push(m) - 1}@@`)
            .replace(/\s+/g, ' ')
            .trim();

        const restore = (text) => text.replace(/@@SQLFMT(\d+)@@/g, (m, i) => store[Number(i)]);
        const head = header.join('\n').trim();
        const prefix = head ? `${head}\n` : '';

        if (restore(body).length <= SQL_MAX_LINE) return prefix + restore(body);

        const call = findOuterCall(body);
        if (call.open >= 0) {
            const args = splitTopLevelCommas(body.slice(call.open + 1, call.close)).join(',\n    ');
            body = `${body.slice(0, call.open + 1)}\n    ${args}\n${body.slice(call.close)}`;
        }

        body = body.replace(/ (from|where|group by|order by|having|limit|offset) /gi, (m, keyword) => `\n${keyword} `);
        return prefix + restore(body).trim();
    } catch (err) {
        return sql;
    }
}

export const EXPORT_FORMATS = [
    { value: 'Excel', label: 'Excel', extension: 'xlsx' },
    { value: 'Csv', label: 'CSV', extension: 'csv' },
    { value: 'Json', label: 'JSON', extension: 'json' },
];

export function formatDateForApi(date) {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function downloadFile(blob, fileName) {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

export async function getErrorMessage(err, fallback) {
    const data = err?.response?.data;
    if (data instanceof Blob) {
        try {
            return JSON.parse(await data.text())?.message || fallback;
        } catch (parseError) {
            return fallback;
        }
    }
    return data?.message || fallback;
}
